import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Star, X } from 'lucide-react';
import { useCVData, type CVHighlight } from '../../../lib/cv-mock-data';
import { useAIGenerateHighlights, useAIEnhanceText } from '../../../hooks/useOnboardingApi';
import { useSparkEnhance, SparkEnhanceEntitlementError } from '../../../hooks/useSparkEnhance';
import { useToast } from '../../../hooks/useToast';
import { WebInput, WebTextarea, WebSaveButton, WebFormDialog, WebItemCard, WebEmptyState, SparkButton, SparkPreview, type SparkScope } from './web-form-elements';
import { validateHighlight } from './cv-form-validation';
import { useHydratedArrayState } from './useHydratedFormState';
import { confirmDeleteCvItem } from './delete-confirmation';
import { highlightListsMatch, pickHighlightSuggestion } from './highlight-suggestion';
import { useDirtyGuard } from '../../../hooks/useDirtyGuard';
import { CvListToolbar } from './CvListToolbar';
import { ReorderableCvList } from './ReorderableCvList';
import { cvChangeNotApplied } from '../../../lib/cv-write-coordinator';

interface EditHighlightsFormProps {
  onSave?: () => Promise<void> | void;
  closeOnSave?: boolean;
  ctaLabel?: string;
  /** Hide skill application when no persisted CV target exists yet. */
  sparkApplySkill?: boolean;
}
function cloneHighlight(item: CVHighlight): CVHighlight { return { ...item }; }

export function EditHighlightsForm({
  onSave,
  closeOnSave = true,
  ctaLabel,
  sparkApplySkill = true,
}: EditHighlightsFormProps) {
  const { t } = useTranslation('seeker');
  const { profile, updateProfile } = useCVData();
  const router = useRouter();
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const generateHighlightsMutation = useAIGenerateHighlights();
  const savedItemsRef = useRef(profile.highlights.map(cloneHighlight));
  const hasLocalEditsRef = useRef(false);
  const enhanceTextMutation = useAIEnhanceText();
  const spark = useSparkEnhance();

  const [items, setItems] = useHydratedArrayState(
    profile.highlights,
    cloneHighlight,
    () => {
      hasLocalEditsRef.current = true;
    },
  );
  useEffect(() => {
    if (!hasLocalEditsRef.current) {
      savedItemsRef.current = profile.highlights.map(cloneHighlight);
    }
  }, [profile.highlights]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<CVHighlight>({ title: '', desc: '' });
  const [showErrors, setShowErrors] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [enhancingField, setEnhancingField] = useState<'title' | 'desc' | null>(null);
  const [pendingTitle, setPendingTitle] = useState<string | null>(null);
  const [pendingDesc, setPendingDesc] = useState<string | null>(null);
  const [suggestedHighlight, setSuggestedHighlight] = useState<CVHighlight | null>(null);

  const enhanceField = async (target: 'title' | 'desc', scope: SparkScope = 'section') => {
    const text = (target === 'title' ? draft.title : draft.desc).trim();
    if (!text || enhancingField) return;
    setEnhancingField(target);
    try {
      await spark.ensureAllowed();
      try {
        const result = await enhanceTextMutation.mutateAsync({
          text: target === 'title' ? draft.title : draft.desc,
          field: target === 'title' ? 'title' : 'highlight',
          scope,
          context: { title: profile.headline },
        });
        if (result.enhanced) {
          if (target === 'title') setPendingTitle(result.enhanced);
          else setPendingDesc(result.enhanced);
        } else {
          toast.error('Generation failed', 'AI returned an empty result. Please try again.');
        }
      } finally {
        await spark.reconcile();
      }
    } catch (err) {
      if (err instanceof SparkEnhanceEntitlementError) return; // upgrade modal opened
      toast.error('Generation failed', err instanceof Error ? err.message : 'Could not enhance highlight');
    } finally {
      setEnhancingField(null);
    }
  };

  const resetPending = () => { setPendingTitle(null); setPendingDesc(null); };
  const errors = validateHighlight(draft, t);
  const hasErrors = Object.keys(errors).length > 0;
  const visibleErrors = showErrors ? errors : {};

  const handleSuggestHighlight = async () => {
    setAiLoading(true);
    try {
      const result = await generateHighlightsMutation.mutateAsync({
        name: profile.name,
        title: profile.headline,
        experience: (profile.experience || []).map((e: any) => ({
          title: e.role || e.title,
          company: e.company,
          achievements: e.bullets || e.achievements || [],
        })),
        certifications: (profile.certifications || []).map((c: any) => ({
          name: c.name,
          issuer: c.issuer,
        })),
        skills: profile.skillGroups?.flatMap((g: any) => g.items || g.skills || []) || [],
        existingHighlights: items.length
          ? items.map((h) => ({ title: h.title, description: h.desc }))
          : undefined,
      });
      if (result.highlights?.length) {
        const generated = result.highlights.map((h: any) => ({
          title: h.title,
          desc: h.description,
        }));
        const suggestion = pickHighlightSuggestion(generated, items);
        if (!suggestion) {
          toast.error(
            t('cv.edit.generateHighlightFailed'),
            t('cv.edit.noNewHighlightSuggestion'),
          );
          return;
        }
        setSuggestedHighlight(suggestion);
      }
    } catch (err) {
      toast.error(
        t('cv.edit.generateHighlightFailed'),
        err instanceof Error ? err.message : t('cv.edit.generateHighlightFailed'),
      );
    } finally {
      setAiLoading(false);
    }
  };

  const openEdit = (i: number) => { setDraft({ ...items[i] }); setEditingIndex(i); setShowErrors(false); resetPending(); };
  const openAdd = () => { setDraft({ title: '', desc: '' }); setEditingIndex(-1); setShowErrors(false); resetPending(); };

  const handleDialogSave = () => {
    if (hasErrors) { setShowErrors(true); return; }
    if (editingIndex === -1) setItems([...items, draft]);
    else if (editingIndex !== null) { const next = [...items]; next[editingIndex] = draft; setItems(next); }
    setEditingIndex(null);
    setShowErrors(false);
  };

  const handleCardDelete = (i: number) => {
    void confirmDeleteCvItem(t, 'highlight', () => setItems(items.filter((_, idx) => idx !== i)));
  };

  const handleSave = async (navigateAfterSave = true): Promise<boolean> => {
    if (isSaving) return false;
    const nextItems = suggestedHighlight ? [...items, suggestedHighlight] : items;
    if (suggestedHighlight) {
      setItems(nextItems);
      setSuggestedHighlight(null);
    }
    updateProfile({ highlights: nextItems });
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave();
        savedItemsRef.current = nextItems.map(cloneHighlight);
        hasLocalEditsRef.current = false;
        toast.success(t('cv.edit.saved'));
        if (navigateAfterSave && closeOnSave && router.canGoBack()) router.back();
      } catch (error) {
        // B02: a CV conflict the user answered is not a failure. "Discard"
        // keeps the version saved elsewhere and closes past the unsaved-
        // changes guard; "keep editing" keeps this draft open.
        const outcome = cvChangeNotApplied(error);
        if (outcome) {
          if (outcome === 'discarded' && closeOnSave) leave();
          return false;
        }
        toast.error(t('cv.edit.saveFailed'));
        return false;
      } finally {
        setIsSaving(false);
      }
    } else {
      savedItemsRef.current = nextItems.map(cloneHighlight);
      hasLocalEditsRef.current = false;
      if (navigateAfterSave) router.back();
    }
    return true;
  };

  const draftChanged = editingIndex !== null && (
    editingIndex === -1
      ? Boolean(draft.title.trim() || draft.desc.trim())
      : !highlightListsMatch([draft], [items[editingIndex]!])
  );
  const isDirty =
    !highlightListsMatch(items, savedItemsRef.current) ||
    suggestedHighlight !== null ||
    draftChanged;
  const { leave } = useDirtyGuard(isDirty, () => handleSave(false));

  const saveSuggestion = () => {
    if (!suggestedHighlight) return;
    setItems((current) => [...current, suggestedHighlight]);
    setSuggestedHighlight(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={handleSuggestHighlight}
          disabled={aiLoading}
          style={{ border: 0, background: 'transparent', color: 'var(--text-secondary)', font: 'inherit', fontSize: 13, fontWeight: 600, cursor: aiLoading ? 'default' : 'pointer', opacity: aiLoading ? 0.55 : 1 }}
        >
          {aiLoading ? t('loading') : t('cv.edit.suggestHighlight')}
        </button>
      </div>
      <CvListToolbar
        addLabel={t('cv.edit.addHighlight')}
        onAdd={openAdd}
        showReorderHint={items.length > 1}
      />
      <ReorderableCvList
        items={items}
        onReorder={setItems}
        keyExtractor={(item) => item.id}
        getItemLabel={(item, index) => item.title || t('cv.edit.highlightNumber', { number: index + 1 })}
        renderItem={({ item, index }) => (
          <WebItemCard
            title={item.title || t('cv.edit.highlightNumber', { number: index + 1 })}
            subtitle={item.desc || t('cv.edit.tapToEdit')}
            icon={<Star size={18} color="var(--text-secondary)" strokeWidth={1.75} />}
            onClick={() => openEdit(index)}
            onDelete={() => handleCardDelete(index)}
          />
        )}
        emptyState={
          <WebEmptyState
            icon={<Star size={32} color="var(--text-tertiary)" strokeWidth={1.25} />}
            text={t('cv.edit.noHighlights')}
          />
        }
      />
      {suggestedHighlight ? (
        <div style={{ border: '1px dashed var(--border)', borderRadius: 18, padding: 16, background: 'var(--surface)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--text-tertiary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {t('cv.edit.suggestedHighlight')}
            </span>
            <button type="button" onClick={() => setSuggestedHighlight(null)} aria-label={t('cv.edit.dismissHighlightSuggestion')} style={{ border: 0, background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4 }}>
              <X size={16} strokeWidth={1.8} />
            </button>
          </div>
          <strong style={{ color: 'var(--text-primary)', fontSize: 15 }}>{suggestedHighlight.title}</strong>
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{suggestedHighlight.desc}</span>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={handleSuggestHighlight} disabled={aiLoading} style={{ flex: 1, height: 38, borderRadius: 9999, border: 0, background: 'var(--hover)', color: 'var(--text-primary)', font: 'inherit', cursor: aiLoading ? 'default' : 'pointer' }}>
              {t('cv.edit.tryAnotherSuggestion')}
            </button>
            <button type="button" onClick={saveSuggestion} style={{ flex: 1, height: 38, borderRadius: 9999, border: 0, background: 'var(--text-primary)', color: 'var(--bg)', font: 'inherit', cursor: 'pointer' }}>
              {t('cv.edit.saveSuggestion')}
            </button>
          </div>
        </div>
      ) : null}
      <WebSaveButton label={ctaLabel ?? t('cv.edit.save')} onClick={() => void handleSave()} loading={isSaving} disabled={isSaving || suggestedHighlight !== null} />

      <WebFormDialog visible={editingIndex !== null} title={editingIndex === -1 ? 'Add highlight' : 'Edit highlight'} onClose={() => { setEditingIndex(null); resetPending(); }}>
        <WebInput label="Title" value={draft.title} onChange={(v) => { setDraft({ ...draft, title: v }); if (pendingTitle) setPendingTitle(null); }} placeholder={t('cv.edit.highlightTitle')} error={visibleErrors.title} />
        {draft.title.trim() ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <SparkButton
              onEnhance={(scope) => enhanceField('title', scope)}
              loading={enhancingField === 'title'}
              disabled={enhancingField !== null}
              showApplySkill={sparkApplySkill}
            />
          </div>
        ) : null}
        {pendingTitle ? (
          <SparkPreview
            text={pendingTitle}
            onAccept={() => { setDraft({ ...draft, title: pendingTitle }); setPendingTitle(null); }}
            onReject={() => setPendingTitle(null)}
          />
        ) : null}
        <WebTextarea label="Impact" value={draft.desc} onChange={(v) => { setDraft({ ...draft, desc: v }); if (pendingDesc) setPendingDesc(null); }} placeholder={t('cv.edit.highlightDesc')} rows={3} error={visibleErrors.desc} />
        {draft.desc.trim() ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <SparkButton
              onEnhance={(scope) => enhanceField('desc', scope)}
              loading={enhancingField === 'desc'}
              disabled={enhancingField !== null}
              showApplySkill={sparkApplySkill}
            />
          </div>
        ) : null}
        {pendingDesc ? (
          <SparkPreview
            text={pendingDesc}
            onAccept={() => { setDraft({ ...draft, desc: pendingDesc }); setPendingDesc(null); }}
            onReject={() => setPendingDesc(null)}
          />
        ) : null}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={handleDialogSave} disabled={showErrors && hasErrors} style={{ flex: 1, height: 48, borderRadius: 9999, backgroundColor: 'var(--text-primary)', color: 'var(--bg)', fontSize: 16, fontWeight: 600, border: 'none', cursor: showErrors && hasErrors ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: showErrors && hasErrors ? 0.5 : 1 }}>
            {editingIndex === -1 ? 'Add' : t('cv.edit.save')}
          </button>
        </div>
      </WebFormDialog>
    </div>
  );
}
